import { ActionIcon, Box, Button, Group, SimpleGrid, Table, Text, Tooltip } from "@mantine/core"
import { CaretDownIcon, CaretRightIcon, InfoIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import type { BuyerPromotion, BuyerPromotionType } from "../../../apis/pricing"
import { DotPill, TableFrame } from "../../delivery/atoms"
import { ListState } from "../../ui"
import classes from "../product-list/catalog.module.css"
import { daysUntil, formatDate, PROMO_KINDS, promoIcon, type PromoKind } from "../product-list/price-labels"

// Buyer promotion page body (canvas OrdB_PromoPage, B27): one supplier's
// active promotions from GET /pricing/buyer/promotions, the ones ending within
// a week on top, then one table per type.

const ENDING_SOON_DAYS = 7
const SECTION_ORDER: BuyerPromotionType[] = [...PROMO_KINDS, "MIXED"]

export type KindFilter = PromoKind | "ALL"

/** Days left: the server's count, else from effectiveTo. */
const daysLeftOf = (p: BuyerPromotion): number | null =>
  p.daysRemaining != null ? Number(p.daysRemaining) : daysUntil(p.effectiveTo)

function Ends({ promotion }: Readonly<{ promotion: BuyerPromotion }>) {
  const { t } = useTranslation("order")
  const days = daysLeftOf(promotion)
  if (days === null) return <Text fz="var(--nw-fz-body)">-</Text>
  const left = days <= 0 ? t("catalog.promotions.endsToday") : t("catalog.promotions.daysLeft", { count: days })
  return (
    <Box>
      {days <= ENDING_SOON_DAYS ? (
        <DotPill tone="red">{left}</DotPill>
      ) : (
        <Text fz="var(--nw-fz-body)" fw={600}>
          {left}
        </Text>
      )}
      <Text className={`${classes.meta} ${classes.mono}`}>{t("catalog.untilDate", { date: formatDate(promotion.effectiveTo) })}</Text>
    </Box>
  )
}

function TypePill({ type }: Readonly<{ type: BuyerPromotionType }>) {
  const { t } = useTranslation("order")
  return (
    <Box component="span" className={classes.pill}>
      {promoIcon(type)}
      {t(`catalog.promoType.${type}`)}
    </Box>
  )
}

function KindChips({ value, onChange }: Readonly<{ value: KindFilter; onChange: (kind: KindFilter) => void }>) {
  const { t } = useTranslation("order")
  return (
    <Group gap="xs">
      {(["ALL", ...PROMO_KINDS] as const).map((k) => (
        <Button
          key={k}
          variant="default"
          radius="md"
          h={42}
          className={classes.toggleChip}
          data-active={value === k || undefined}
          aria-pressed={value === k}
          onClick={() => onChange(k)}
        >
          {k === "ALL" ? t("catalog.promotions.all") : t(`catalog.promoType.${k}`)}
        </Button>
      ))}
    </Group>
  )
}

function EndingSoon({ rows, supplierName }: Readonly<{ rows: BuyerPromotion[]; supplierName: string }>) {
  const { t } = useTranslation("order")
  if (rows.length === 0) return null
  return (
    <Box mb="lg">
      <Group justify="space-between" mb="xs">
        <Text fz="var(--nw-fz-card)" fw={650}>
          {t("catalog.promotions.endingSoon")}
        </Text>
        <Text className={classes.meta}>{t("catalog.promotions.endingSoonHint")}</Text>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {rows.map((p) => (
          <Box key={p.id} className={classes.promoCard}>
            <Group justify="space-between" wrap="nowrap">
              <TypePill type={p.type} />
              <Text span className={`${classes.meta} ${classes.mono}`}>
                {t("catalog.untilDate", { date: formatDate(p.effectiveTo) })}
              </Text>
            </Group>
            <Text fz="var(--nw-fz-lead)" fw={650} lineClamp={1}>
              {p.name || "-"}
            </Text>
            <Text className={classes.meta} lineClamp={1}>
              {supplierName || "-"}
            </Text>
            <Ends promotion={p} />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  )
}

function PromotionRow({ promotion, supplierName }: Readonly<{ promotion: BuyerPromotion; supplierName: string }>) {
  const { t } = useTranslation("order")
  const [open, setOpen] = React.useState(false)
  const variants = promotion.variants || []
  const label = open ? t("catalog.promotions.hideProducts") : t("catalog.promotions.showProducts")
  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Tooltip label={label} withArrow disabled={variants.length === 0}>
            <ActionIcon
              variant="light"
              color="gray"
              size={32}
              radius="md"
              aria-label={label}
              aria-expanded={open}
              disabled={variants.length === 0}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <CaretDownIcon size={18} /> : <CaretRightIcon size={18} />}
            </ActionIcon>
          </Tooltip>
        </Table.Td>
        <Table.Td>
          <Text fz="var(--nw-fz-body)" fw={650}>
            {promotion.name || "-"}
          </Text>
          {promotion.summary && <Text className={classes.meta}>{promotion.summary}</Text>}
        </Table.Td>
        <Table.Td>
          <Text fz="var(--nw-fz-body)">{supplierName || "-"}</Text>
        </Table.Td>
        <Table.Td>
          <Text fz="var(--nw-fz-body)">
            {variants.length > 0 ? t("catalog.productsCount", { count: variants.length }) : t("catalog.promotions.allProducts")}
          </Text>
        </Table.Td>
        <Table.Td>
          <Ends promotion={promotion} />
        </Table.Td>
      </Table.Tr>
      {open && (
        <Table.Tr>
          <Table.Td />
          <Table.Td colSpan={4}>
            <Box component="ul" m={0} pl="md">
              {variants.map((v) => (
                <Box component="li" key={v.variantId}>
                  <Text span fz="var(--nw-fz-body)">
                    {v.name || "-"}
                  </Text>
                  {v.skuCode && (
                    <Text span className={`${classes.meta} ${classes.mono}`} ml="sm">
                      {v.skuCode}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

function TypeSection({ type, rows, supplierName }: Readonly<{ type: BuyerPromotionType; rows: BuyerPromotion[]; supplierName: string }>) {
  const { t } = useTranslation("order")
  return (
    <Box mb="md">
      <TableFrame>
        <Group justify="space-between" px="md" py="sm">
          <TypePill type={type} />
          <Text className={classes.meta}>{t("catalog.promotions.count", { count: rows.length })}</Text>
        </Group>
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead className={classes.promoHead}>
              <Table.Tr>
                <Table.Th w={72}>{t("catalog.col.action")}</Table.Th>
                <Table.Th>{t("catalog.promotions.colPromotion")}</Table.Th>
                <Table.Th>{t("catalog.promotions.colSupplier")}</Table.Th>
                <Table.Th>{t("catalog.promotions.colScope")}</Table.Th>
                <Table.Th>{t("catalog.promotions.colEnds")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((p) => (
                <PromotionRow key={p.id} promotion={p} supplierName={supplierName} />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </TableFrame>
    </Box>
  )
}

type Props = {
  rows: BuyerPromotion[]
  state: "loading" | "error" | "denied" | "data"
  supplierName: string
  /** Supplier filter (shared with the catalogue pick). */
  supplierPicker: React.ReactNode
  kind: KindFilter
  onKindChange: (kind: KindFilter) => void
  onRetry: () => void
}

export function BuyerPromotionsView({ rows, state, supplierName, supplierPicker, kind, onKindChange, onRetry }: Props) {
  const { t } = useTranslation("order")
  const visible = kind === "ALL" ? rows : rows.filter((p) => p.type === kind)
  const endingSoon = visible.filter((p) => {
    const d = daysLeftOf(p)
    return d !== null && d <= ENDING_SOON_DAYS
  })

  const body = (() => {
    if (state === "loading") return <TableFrame><ListState state="loading" /></TableFrame>
    if (state === "denied") {
      return (
        <TableFrame>
          <ListState state="empty" title={t("catalog.promotions.accessDenied")} />
        </TableFrame>
      )
    }
    if (state === "error") {
      return (
        <TableFrame>
          <ListState state="error" title={t("catalog.promotions.loadFailed")} onRetry={onRetry} />
        </TableFrame>
      )
    }
    if (visible.length === 0) {
      return (
        <TableFrame>
          <ListState state="empty" title={t("catalog.promotions.empty")} description={t("catalog.promotions.emptyHint")} />
        </TableFrame>
      )
    }
    return (
      <>
        <EndingSoon rows={endingSoon} supplierName={supplierName} />
        {SECTION_ORDER.filter((type) => visible.some((p) => p.type === type)).map((type) => (
          <TypeSection key={type} type={type} rows={visible.filter((p) => p.type === type)} supplierName={supplierName} />
        ))}
        <Box className={classes.notice}>
          <InfoIcon size={18} />
          <Text span inherit>
            {t("catalog.promotions.infoNote")}
          </Text>
        </Box>
      </>
    )
  })()

  return (
    <Box>
      <Group align="flex-end" gap="lg" mb="lg" wrap="wrap">
        <Box>
          <Text fz="var(--nw-fz-body)" fw={500} c="var(--nw-text-2)" mb={4}>
            {t("catalog.supplierLabel")}
          </Text>
          {supplierPicker}
        </Box>
        <Box>
          <Text fz="var(--nw-fz-body)" fw={500} c="var(--nw-text-2)" mb={4}>
            {t("catalog.promotions.typeLabel")}
          </Text>
          <KindChips value={kind} onChange={onKindChange} />
        </Box>
      </Group>
      {body}
    </Box>
  )
}
